import { Modal, FormInstance } from "antd";
import { observable, makeObservable, actionBound } from "mobx";
import { cloneDeep, groupBy } from "lodash";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillPrinter from "@/services/print/waybill/WaybillPrinter";
import { uuid, confirm } from "@/utils";
import { ModalInstance, ModalStore } from "@/hooks/Modal/ModalStore";
import PrintProcessingModal from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/PrintProcessingModal";
import EwaybillResultStore from "@/scenes/OrderManagement/components/dialogs/PrintEwaybillResultDialog/EwaybillResultStore";
import GetWaybillNoMethod from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/GetWaybillNoMethod";
import {
    EwaybillGenerateResult,
    PackageCountType,
    ShippingAddressSelectType,
    submitAppendExtraWaybillToSellerRemarksTask,
} from "@/services/api/ewaybillGenerate";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import { logReprintWaybill } from "@/services/api/order";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import MultiShopSelectStore from "@/components/MultiShopSelect/MultiShopSelectStore";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import ShopType from "@/enum/ShopType";
import { UpdateEwaybillAccountQuantityPostData } from "@/services/api/setting/ewaybillAccount";
import { sortEwaybills } from "@/utils/OrderUtils";
import { checkGenerateWaybillNoOnlyOrders } from "@/utils/printCheck";
import OrderTag from "@/enum/OrderTag";
import { getAliOutOrderTagInfo } from "@/utils/OrderTagUtils";
import { getPackageCountTypeByCopies } from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/util";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import type GlobalStore from "@/store";
import MultiPlatformEwaybillResultStore from "../../MultiPlatformPrintEwaybillResultDialog/MultiPlatformEwaybillResultStore";
import PrintValidator from "../utils/PrintValidator";
import PrintEwaybillResultModal from "../../MultiPlatformPrintEwaybillResultDialog/PrintEwaybillResultModal";
import { PrintWaybillAdvancedSetting } from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/components/AdvancedSettingFormItem";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import { PrintableOrder } from "@/services/print/waybill/impl/AbstractEwaybillPrinter";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { collectEvent } from "@/services/dataTrack";
import { EwaybillCopiesPreset } from "@/components/EwaybillCopiesSelector";
import { getPrintBatchNo, PrintBatchNoLevel } from "@/services/api/ewaybill";
import { PlatformSelectedTemplateInfos, PrintWaybillFormValues, ShippingAddressMatchRule } from "../PrintWaybillForm";
import type SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import { transformOtherDropShippingSupplierSourceShopType2PlatformType } from "../utils/otherDropShippingSupplier";
import StorageManager from "@/utils/StorageManager";

interface RangeInfo {
    orders: OrderDTO[];
    firstDocumentNumber?: number;
    printableOrders?: PrintableOrder[];
    totalDocumentCount?: number;
}

function getShippingAddressSelectType(shippingAddressMatchRule?: ShippingAddressMatchRule) {
    if (shippingAddressMatchRule === ShippingAddressMatchRule.SHOP) {
        return ShippingAddressSelectType.BY_SHOP_BINDING;
    }

    if (shippingAddressMatchRule === ShippingAddressMatchRule.TEMPLATE) {
        return ShippingAddressSelectType.BY_TEMPLATE_BINDING;
    }

    return undefined;
}

export interface PlatformOrderInfo {
    [MultiShopSelectPlatformType.tb]: OrderDTO[];
    [MultiShopSelectPlatformType.pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.dy]: OrderDTO[];
    [MultiShopSelectPlatformType.jd]: OrderDTO[];
    [MultiShopSelectPlatformType.ks]: OrderDTO[];
    [MultiShopSelectPlatformType.ali]: OrderDTO[];
    [MultiShopSelectPlatformType.xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.channelShop]: OrderDTO[];
    [MultiShopSelectPlatformType.wb]: OrderDTO[];
    [MultiShopSelectPlatformType.other]: OrderDTO[];

    [MultiShopSelectPlatformType.other_drop_shipping_supplier_ks]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_wb]: OrderDTO[];
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_other]: OrderDTO[];

    [MultiShopSelectPlatformType.ali_out_order_taobao]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_dy]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_kwai]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_jd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_out_order_weixin]: OrderDTO[];

    [MultiShopSelectPlatformType.ali_plaintext_out_order_taobao]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_pdd]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_dy]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_kwai]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_xhs]: OrderDTO[];
    [MultiShopSelectPlatformType.ali_plaintext_out_order_jd]: OrderDTO[];
}

export const platforms = Object.keys(MultiShopSelectPlatformType) as MultiShopSelectPlatformType[];

interface MultiPlatformPrintWaybillModalStoreParams {
    globalStore: typeof GlobalStore;
    modalStore: ModalStore;
    orders: OrderDTO[];
    form: FormInstance<PrintWaybillFormValues>;
    defaultTemplateId?: number;
    onCancel?: () => void;
    onShipTaskDone?: () => void;
    onUpdateOrder?: (id: number, params: Partial<OrderDTO>) => void;
    multiPlatformModalOrderOriginCheck?: (order: OrderDTO) => ShopType;
    onUpdateOrderSellerRemarks?: (orderId: number, sellerRemarks: OrderSellerRemarksDTO) => void;
    onUpdateSupplierOrderRemarks?: (supplierOrderId: number, dropshippingRemarks: string) => void;
    shopId?: number;
    canUseMaxQuantityPerPackageOrderCount?: number;
    /** 按照区间顺序进行生成面单和打印 */
    processInRange?: boolean;
    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

class MultiPlatformPrintWaybillModalStore {
    public globalStore: typeof GlobalStore;
    public modalStore: ModalStore;
    public orders: OrderDTO[];
    public form: FormInstance<PrintWaybillFormValues>;
    public defaultTemplateId?: number;
    public onCancel?: () => void;
    public onShipTaskDone?: () => void;
    public onUpdateOrder?: (id: number, params: Partial<OrderDTO>) => void;
    public multiPlatformModalOrderOriginCheck?: (order: OrderDTO) => ShopType;
    public onUpdateOrderSellerRemarks?: (orderId: number, sellerRemarks: OrderSellerRemarksDTO) => void;
    public onUpdateSupplierOrderRemarks?: (supplierOrderId: number, dropshippingRemarks: string) => void;
    public shopId?: number;
    public canUseMaxQuantityPerPackageOrderCount?: number;
    public supplierPartnershipsStore?: SupplierPartnershipsStore;

    platformOrderInfo: PlatformOrderInfo = {
        [MultiShopSelectPlatformType.tb]: [],
        [MultiShopSelectPlatformType.pdd]: [],
        [MultiShopSelectPlatformType.dy]: [],
        [MultiShopSelectPlatformType.jd]: [],
        [MultiShopSelectPlatformType.ks]: [],
        [MultiShopSelectPlatformType.ali]: [],
        [MultiShopSelectPlatformType.xhs]: [],
        [MultiShopSelectPlatformType.channelShop]: [],
        [MultiShopSelectPlatformType.wb]: [],
        [MultiShopSelectPlatformType.other]: [],
        [MultiShopSelectPlatformType.other_drop_shipping_supplier_ks]: [],
        [MultiShopSelectPlatformType.other_drop_shipping_supplier_xhs]: [],
        [MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop]: [],
        [MultiShopSelectPlatformType.other_drop_shipping_supplier_wb]: [],
        [MultiShopSelectPlatformType.other_drop_shipping_supplier_other]: [],
        [MultiShopSelectPlatformType.ali_out_order_taobao]: [],
        [MultiShopSelectPlatformType.ali_out_order_pdd]: [],
        [MultiShopSelectPlatformType.ali_out_order_dy]: [],
        [MultiShopSelectPlatformType.ali_out_order_kwai]: [],
        [MultiShopSelectPlatformType.ali_out_order_xhs]: [],
        [MultiShopSelectPlatformType.ali_out_order_jd]: [],
        [MultiShopSelectPlatformType.ali_out_order_weixin]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_taobao]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_pdd]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_dy]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_kwai]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_xhs]: [],
        [MultiShopSelectPlatformType.ali_plaintext_out_order_jd]: [],
    };

    orderPlatformMap = new Map<number, MultiShopSelectPlatformType>();

    multiPlatformEwaybillResultStore: MultiPlatformEwaybillResultStore;

    taskId = uuid();

    constructor(params: MultiPlatformPrintWaybillModalStoreParams) {
        makeObservable(this, {
            platformOrderInfo: observable,
            orderPlatformMap: observable,
            multiPlatformEwaybillResultStore: observable,
            taskId: observable,
            initPlatformOrderInfo: actionBound,
            updateOrderTable: actionBound,
            batchUpdateEwaybillQuantity: actionBound,
            logRePrint: actionBound,
            handlePrintBtnClick: actionBound,
            addOrderShippingAddress: actionBound,
            generatePrintBatchNoIfNeed: actionBound,
            collectGeneratePrintEvent: actionBound,
            rangeOrdersByShopSequence: actionBound,
            validateForm: actionBound,
            validatePlatforms: actionBound,
            checkPrinters: actionBound,
            validateChildWaybillMode: actionBound,
            confirmCopies: actionBound,
            calculatePrintParameters: actionBound,
            initializePrintingProcess: actionBound,
            processPlatformEwaybillsInRange: actionBound,
            setupEwaybillResultDocumentNumbers: actionBound,
            setupEwaybillResultDocumentNumbersByRange: actionBound,
            openPrintResultModal: actionBound,
            executePrinting: actionBound,
            executeRangePrinting: actionBound,
        });
        this.globalStore = params.globalStore;
        this.modalStore = params.modalStore;
        this.orders = params.orders;
        this.form = params.form;
        this.defaultTemplateId = params.defaultTemplateId;
        this.onCancel = params.onCancel;
        this.onShipTaskDone = params.onShipTaskDone;
        this.onUpdateOrder = params.onUpdateOrder;
        this.multiPlatformModalOrderOriginCheck = params.multiPlatformModalOrderOriginCheck;
        this.onUpdateOrderSellerRemarks = params.onUpdateOrderSellerRemarks;
        this.onUpdateSupplierOrderRemarks = params.onUpdateSupplierOrderRemarks;
        this.shopId = params.shopId;
        this.canUseMaxQuantityPerPackageOrderCount = params.canUseMaxQuantityPerPackageOrderCount;
        this.supplierPartnershipsStore = params.supplierPartnershipsStore;

        this.initPlatformOrderInfo();

        this.multiPlatformEwaybillResultStore = new MultiPlatformEwaybillResultStore(this.globalStore);
    }

    initPlatformOrderInfo() {
        const defaultCheck = (order: OrderDTO) => this.globalStore.shopMapping.get(order.shopId)?.type;
        const checkFn = this.multiPlatformModalOrderOriginCheck || defaultCheck;

        // 重置所有平台订单数组
        Object.keys(this.platformOrderInfo).forEach((key) => {
            this.platformOrderInfo[key as MultiShopSelectPlatformType] = [];
        });

        // 分类订单到各个平台
        this.orders.forEach((order) => {
            const orderShopType = defaultCheck(order);

            if (orderShopType === ShopType.OTHER_DROP_SHIPPING_SUPPLIER) {
                const platform = transformOtherDropShippingSupplierSourceShopType2PlatformType(order.sourceShopType);

                this.platformOrderInfo[platform].push(order);
                this.orderPlatformMap.set(order.id, platform);

                return;
            }

            const shopType = checkFn(order);

            if (MultiShopSelectStore.tbShopTypes.includes(shopType)) {
                this.platformOrderInfo.tb.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.tb);
            } else if (MultiShopSelectStore.pddShopTypes.includes(shopType)) {
                this.platformOrderInfo.pdd.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.pdd);
            } else if (MultiShopSelectStore.dyShopTypes.includes(shopType)) {
                this.platformOrderInfo.dy.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.dy);
            } else if (MultiShopSelectStore.jdShopTypes.includes(shopType)) {
                this.platformOrderInfo.jd.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.jd);
            } else if (MultiShopSelectStore.ksShopTypes.includes(shopType)) {
                this.platformOrderInfo.ks.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ks);
            } else if (MultiShopSelectStore.aliShopTypes.includes(shopType)) {
                const orderTagInfo = getAliOutOrderTagInfo(order);

                if (orderTagInfo) {
                    switch (orderTagInfo) {
                        case OrderTag.ALI_OUT_ORDER_TAOBAO:
                            this.platformOrderInfo.ali_out_order_taobao.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_taobao);
                            break;
                        case OrderTag.ALI_OUT_ORDER_PDD:
                            this.platformOrderInfo.ali_out_order_pdd.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_pdd);
                            break;
                        case OrderTag.ALI_OUT_ORDER_DY:
                            this.platformOrderInfo.ali_out_order_dy.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_dy);
                            break;
                        case OrderTag.ALI_OUT_ORDER_KWAI:
                            this.platformOrderInfo.ali_out_order_kwai.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_kwai);
                            break;
                        case OrderTag.ALI_OUT_ORDER_XHS:
                            this.platformOrderInfo.ali_out_order_xhs.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_xhs);
                            break;
                        case OrderTag.ALI_OUT_ORDER_JD:
                            this.platformOrderInfo.ali_out_order_jd.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_jd);
                            break;
                        case OrderTag.ALI_OUT_ORDER_WEIXIN:
                            this.platformOrderInfo.ali_out_order_weixin.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_out_order_weixin);
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_TAOBAO:
                            this.platformOrderInfo.ali_plaintext_out_order_taobao.push(order);
                            MultiShopSelectPlatformType.ali_plaintext_out_order_taobao;
                            this.orderPlatformMap.set(
                                order.id,
                                MultiShopSelectPlatformType.ali_plaintext_out_order_taobao,
                            );
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_PDD:
                            this.platformOrderInfo.ali_plaintext_out_order_pdd.push(order);
                            this.orderPlatformMap.set(
                                order.id,
                                MultiShopSelectPlatformType.ali_plaintext_out_order_pdd,
                            );
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_DY:
                            this.platformOrderInfo.ali_plaintext_out_order_dy.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_plaintext_out_order_dy);
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_KWAI:
                            this.platformOrderInfo.ali_plaintext_out_order_kwai.push(order);
                            MultiShopSelectPlatformType.ali_plaintext_out_order_kwai;
                            this.orderPlatformMap.set(
                                order.id,
                                MultiShopSelectPlatformType.ali_plaintext_out_order_kwai,
                            );
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_XHS:
                            this.platformOrderInfo.ali_plaintext_out_order_xhs.push(order);
                            this.orderPlatformMap.set(
                                order.id,
                                MultiShopSelectPlatformType.ali_plaintext_out_order_xhs,
                            );
                            break;
                        case OrderTag.ALI_PLAINTEXT_OUT_ORDER_JD:
                            this.platformOrderInfo.ali_plaintext_out_order_jd.push(order);
                            this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali_plaintext_out_order_jd);
                            break;
                        default:
                            break;
                    }
                } else {
                    this.platformOrderInfo.ali.push(order);
                    this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.ali);
                }
            } else if (MultiShopSelectStore.xhsShopTypes.includes(shopType)) {
                this.platformOrderInfo.xhs.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.xhs);
            } else if (MultiShopSelectStore.channelShopTypes.includes(shopType)) {
                this.platformOrderInfo.channelShop.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.channelShop);
            } else if (MultiShopSelectStore.wbShopTypes.includes(shopType)) {
                this.platformOrderInfo.wb.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.wb);
            } else {
                this.platformOrderInfo.other.push(order);
                this.orderPlatformMap.set(order.id, MultiShopSelectPlatformType.other);
            }
        });
    }

    async updateOrderTable(
        platform: MultiShopSelectPlatformType,
        orders: OrderDTO[],
        template: WaybillTemplateDTO,
        waybillGenerateResults: EwaybillGenerateResult[],
        isReprint: boolean,
        isGenerateWaybillNoOnly: boolean,
    ) {
        const orderMap: { [key in number]: OrderDTO } = {};

        orders.forEach((order) => {
            orderMap[order.id] = order;
        });

        const needAppendToRemarkOrders: {
            shopId: number;
            orderId: number;
        }[] = [];

        waybillGenerateResults.forEach((result) => {
            // 本次打印的运单号
            const ewaybills: Partial<EwaybillDTO>[] = [...(result.ewaybills || [])];

            if (ewaybills && ewaybills.length > 0) {
                const order = orderMap[result.orderId];

                const prevEwaybillLength = order.ewaybills?.length || 0;

                if (ewaybills.length > 1) {
                    ewaybills.reverse(); // 倒序，最后生成的单号排在前面
                }

                let newEwaybills: any[];

                if (!isReprint) {
                    newEwaybills = ewaybills.concat(order.ewaybills);
                } else {
                    newEwaybills = cloneDeep(order.ewaybills) || [];

                    // 更新对应的ewaybills
                    ewaybills.forEach((ewaybill) => {
                        const index = newEwaybills.findIndex((it) => it.waybillNo === ewaybill.waybillNo);

                        if (index > -1) {
                            newEwaybills[index] = ewaybill;
                        } else {
                            newEwaybills.push(ewaybill);
                        }
                    });
                }

                const nextEwaybillLength = newEwaybills.length;

                newEwaybills.forEach((ewaybill: EwaybillDTO) => {
                    const ewaybillResultStore = this.multiPlatformEwaybillResultStore[platform];

                    if (!ewaybillResultStore) return;

                    const printIndex = ewaybillResultStore.printIndexMapping.get(ewaybill.waybillNo);

                    if (printIndex) {
                        ewaybill.printIndex = printIndex;
                    }
                });

                if (nextEwaybillLength > prevEwaybillLength) {
                    needAppendToRemarkOrders.push({
                        shopId: order.shopId,
                        orderId: order.id,
                    });
                }

                const sequenceNo = order?.sequenceNo;

                const mainEwaybill = ewaybills[0];

                const waybillNo = mainEwaybill.parentWaybillNo || mainEwaybill.waybillNo;

                sortEwaybills(newEwaybills, waybillNo);

                const newProps: any = {
                    hasEwaybillNo: true,

                    logisticsCompany: template.logisticsCompany,

                    waybillPrintTime: mainEwaybill.dateCreated,

                    ewaybills: newEwaybills,

                    waybillNo,

                    sequenceNo,
                };

                if (!isGenerateWaybillNoOnly) {
                    newProps.isWaybillPrinted = true;

                    newProps.waybillPrintCount = order.waybillPrintCount + 1;
                }

                this.onUpdateOrder?.(order.id, newProps);
            }
        });

        if (!needAppendToRemarkOrders?.length) {
            return;
        }

        // 跨平台，多店铺需要对订单进行店铺分组后，才能进行订单追加备注
        // 根据店铺分组
        const grouped = groupBy(needAppendToRemarkOrders, (it) => it.shopId);
        const needAppendToRemarkOrdersByShopChunks = Object.values(grouped);

        // 单店铺需要订单追加备注的订单列表
        for (const needAppendToRemarkOrdersBySingleShop of needAppendToRemarkOrdersByShopChunks) {
            const shopId = needAppendToRemarkOrdersBySingleShop[0].shopId;

            let shopAdvancedSetting = this.globalStore.shopAdvancedSettingStore.mapping.get(shopId);

            if (!shopAdvancedSetting) {
                shopAdvancedSetting = await this.globalStore.shopAdvancedSettingStore.getAdvancedSetting(shopId);
            }

            // 开启配置才追加订单
            if (shopAdvancedSetting.appendExtraWaybillNoToSellerRemarks) {
                const orderIds = needAppendToRemarkOrdersBySingleShop.map((it) => it.orderId);

                submitAppendExtraWaybillToSellerRemarksTask(shopId, orderIds);
            }
        }
    }

    /**
     * 更新电子面单账号余额
     */
    async batchUpdateEwaybillQuantity(
        data: Pick<UpdateEwaybillAccountQuantityPostData, "allocatedQuantity" | "ewaybillShippingAddressId">[],
    ) {
        if (!data?.length) {
            return;
        }

        this.globalStore.ewaybillAccountStore.batchUpdateEwaybillAccountQuantity(data);
    }

    async logRePrint(
        platform: MultiShopSelectPlatformType,
        ewaybillsGenerateResults: EwaybillGenerateResult[],
        template: WaybillTemplateDTO,
        copies: number,
        isGenerateWaybillNoOnly: boolean,
        generateAndPrintSameTime: boolean,
        ignoreDuplicateOrderValidation: boolean,
        printerName: string,
        shippingAddressSelectType?: ShippingAddressSelectType,
    ) {
        const ewaybillsMap = new Map<number, EwaybillGenerateResult>();

        for (let i = 0; i < ewaybillsGenerateResults.length; i++) {
            const ewaybillResult = ewaybillsGenerateResults[i];

            ewaybillsMap.set(ewaybillResult.orderId, ewaybillResult);
        }

        for (let i = 0; i < this.orders.length; i++) {
            const order = this.orders[i];

            try {
                const ewaybillResult = ewaybillsMap.get(order.id);

                if (!ewaybillResult?.errors?.length && ewaybillResult?.ewaybills?.length) {
                    await logReprintWaybill({
                        copies,
                        ewaybills: ewaybillResult.ewaybills,
                        logisticsCompany: template.logisticsCompany,
                        templateName: template.name,
                        order,
                        isGenerateWaybillNoOnly,
                        generateAndPrintSameTime,
                        ignoreDuplicateOrderValidation,
                        printerName,
                        shippingAddressSelectType,
                    });

                    this.updateOrderTable(platform, [order], template, [ewaybillResult], true, isGenerateWaybillNoOnly);
                }
            } catch (error) {}
        }
    }

    async handlePrintBtnClick() {
        // 1. 校验表单
        const formValues = await this.validateForm();
        if (!formValues) return;

        // 2. 提取表单值
        const {
            getWaybillNoMethod,
            shippingAddress,
            copies,
            isGenerateWaybillNoOnly,
            printGiftInLastWaybill,
            advancedSetting,
            specifiedSplitQuantity,
            maxWeightPerPackage,
            shippingAddressMatchRule,
            defaultUnitWeight,
        } = formValues;
        const shippingAddressSelectType = getShippingAddressSelectType(shippingAddressMatchRule);

        // 3. 校验订单
        await checkGenerateWaybillNoOnlyOrders(this.orders, getWaybillNoMethod);

        // 4. 校验平台规则
        const platformValidationPassed = await this.validatePlatforms(formValues);
        if (!platformValidationPassed) return;

        // 5. 检查打印机
        const printerCheckPassed = await this.checkPrinters(formValues, isGenerateWaybillNoOnly);
        if (!printerCheckPassed) return;

        // 6. 校验子母单模式
        const childWaybillValidationPassed = this.validateChildWaybillMode(formValues, copies, getWaybillNoMethod);
        if (!childWaybillValidationPassed) return;

        // 7. 确认打印份数
        const copiesConfirmed = await this.confirmCopies(this.orders, formValues);
        if (!copiesConfirmed) return;

        // 8. 计算实际打印参数
        const { realCopies, packageCountType, useMaxQuantityPerPackage } = this.calculatePrintParameters(copies);

        const processInRange = advancedSetting?.processInRange;

        // 10. 初始化打印流程
        const processModal = this.initializePrintingProcess(
            realCopies,
            shippingAddress,
            isGenerateWaybillNoOnly,
            useMaxQuantityPerPackage,
            processInRange,
        );

        this.addOrderShippingAddress(formValues);

        try {
            for (const platform of platforms) {
                if (!this.platformOrderInfo[platform]?.length) continue;
                const { template } = formValues[platform];

                StorageManager.setLastWaybillTemplateId(platform, template?.id);
            }
        } catch {}

        const printBatchNo = await this.generatePrintBatchNoIfNeed(getWaybillNoMethod);

        const timeStart = new Date().getTime();

        if (processInRange) {
            let rangeInfos: RangeInfo[];

            try {
                rangeInfos = await this.processPlatformEwaybillsInRange({
                    formValues,
                    realCopies,
                    isGenerateWaybillNoOnly,
                    printGiftInLastWaybill,
                    advancedSetting,
                    packageCountType,
                    getWaybillNoMethod,
                    processModal,
                    useMaxQuantityPerPackage,
                    printBatchNo,
                    specifiedSplitQuantity,
                    maxWeightPerPackage,
                    defaultUnitWeight,
                    shippingAddressSelectType,
                });
            } finally {
                processModal.onCancel();
            }

            console.log("rangeInfos:", rangeInfos);

            this.openPrintResultModal();

            if (!isGenerateWaybillNoOnly) {
                await this.executeRangePrinting(realCopies, shippingAddress, rangeInfos);
            }
        } else {
            try {
                // 11. 处理各平台电子面单生成
                await this.processPlatformEwaybills({
                    formValues,
                    realCopies,
                    isGenerateWaybillNoOnly,
                    printGiftInLastWaybill,
                    advancedSetting,
                    packageCountType,
                    getWaybillNoMethod,
                    processModal,
                    useMaxQuantityPerPackage,
                    printBatchNo,
                    specifiedSplitQuantity,
                    maxWeightPerPackage,
                    defaultUnitWeight,
                    shippingAddressSelectType,
                });
            } finally {
                processModal.onCancel();
            }

            this.openPrintResultModal();

            if (!isGenerateWaybillNoOnly) {
                await this.executePrinting(realCopies, shippingAddress);
            }
        }

        if (!isGenerateWaybillNoOnly) {
            const timeEnd = new Date().getTime();

            this.collectGeneratePrintEvent({
                tt: timeEnd - timeStart,
                processInRange,
                getWaybillNoMethod,
            });
        }
    }

    /**
     * 订单添加需要打印的发货人信息
     */
    addOrderShippingAddress(formValues: PrintWaybillFormValues) {
        const { shippingAddressMatchRule, shippingAddress } = formValues;
        const shippingAddressMapping = this.globalStore.waybillPrintStore.shippingAddressMapping;

        const manualShopTypes = [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL];

        const getManualOrderShippingAddress = (order: OrderDTO) => {
            if (!order.sellerRemarks?.shippingAddressId) return;
            const shop = this.globalStore.shopMapping.get(order.shopId);
            if (!manualShopTypes.includes(shop?.type)) return;
            return shippingAddressMapping.get(order.sellerRemarks.shippingAddressId);
        };

        const setupOrderShippingAddress = (
            order: OrderDTO,
            getMatchedShippingAddress?: () => ShippingAddressDTO | undefined,
        ) => {
            const supplierShippingAddress = this.getSupplierOrderShippingAddress(order);

            if (supplierShippingAddress) {
                order._shippingAddress = supplierShippingAddress;
                return;
            }

            const manualOrderShippingAddress = getManualOrderShippingAddress(order);

            if (manualOrderShippingAddress) {
                order._shippingAddress = manualOrderShippingAddress;
                return;
            }

            const matchedShippingAddress = getMatchedShippingAddress?.();

            if (matchedShippingAddress) {
                order._shippingAddress = matchedShippingAddress;
            }
        };

        if (shippingAddressMatchRule) {
            const shippingAddresses = this.globalStore.waybillPrintStore.shippingAddresses || [];

            const bindingShopIdAddressMap = new Map<number, ShippingAddressDTO>();
            for (const shippingAddress of shippingAddresses) {
                const { bindingShopIdList } = shippingAddress;
                if (!bindingShopIdList?.length) continue;
                for (const shopId of bindingShopIdList) {
                    if (!bindingShopIdAddressMap.has(shopId)) {
                        bindingShopIdAddressMap.set(shopId, shippingAddress);
                    }
                }
            }

            const platformTemplateDefaultAddressMap = new Map<MultiShopSelectPlatformType, ShippingAddressDTO>();
            for (const platform of platforms) {
                const { template } = (formValues[platform] || {}) as PlatformSelectedTemplateInfos;
                if (!template?.defaultShippingAddressId) continue;
                const shippingAddress = shippingAddressMapping.get(template.defaultShippingAddressId);
                if (shippingAddress) {
                    platformTemplateDefaultAddressMap.set(platform, shippingAddress);
                }
            }

            for (const platform of platforms) {
                const orders = this.platformOrderInfo[platform] || [];

                orders.forEach((order) => {
                    setupOrderShippingAddress(order, () => {
                        if (shippingAddressMatchRule === ShippingAddressMatchRule.SHOP) {
                            return bindingShopIdAddressMap.get(order.shopId) || shippingAddress;
                        }

                        if (shippingAddressMatchRule === ShippingAddressMatchRule.TEMPLATE) {
                            const platform = this.orderPlatformMap.get(order.id);
                            const templateShippingAddress = platform
                                ? platformTemplateDefaultAddressMap.get(platform)
                                : undefined;

                            return templateShippingAddress || shippingAddress;
                        }

                        return shippingAddress;
                    });
                });
            }
        } else {
            const orders: OrderDTO[] = [];

            platforms.forEach((platform) => {
                orders.push(...(this.platformOrderInfo[platform] || []));
            });

            if (!orders?.length) return;

            orders.forEach((order) => {
                setupOrderShippingAddress(order);
            });
        }
    }

    getSupplierOrderShippingAddress(order: OrderDTO) {
        const retailerShopId = order.supplierDropShippingInfo?.retailerShopId;
        if (!retailerShopId) return;

        const partnerships = this.supplierPartnershipsStore?.supplierPartnershipsMap.get(order.shopId);
        const partnership = partnerships?.find((partnership) => partnership.shopId === retailerShopId);

        return partnership?.shippingAddress;
    }

    async generatePrintBatchNoIfNeed(getWaybillNoMethod: GetWaybillNoMethod) {
        if (getWaybillNoMethod !== GetWaybillNoMethod.NEW) return null;

        const { batchNo } = await getPrintBatchNo({
            level: PrintBatchNoLevel.COMPANY,
        });

        return batchNo;
    }

    collectGeneratePrintEvent({
        tt,
        processInRange,
        getWaybillNoMethod,
    }: {
        tt: number;
        processInRange: boolean;
        getWaybillNoMethod: GetWaybillNoMethod;
    }) {
        try {
            let count = 0;

            for (const platform of platforms) {
                const store = this.multiPlatformEwaybillResultStore[platform];

                if (!store) continue;

                count += store.printableOrders.length || 0;
            }

            if (!count) return;

            const totalTime = parseFloat((tt / 1000).toFixed(1));

            let content = (getWaybillNoMethod === GetWaybillNoMethod.NEW ? "获取新单号" : "获取旧单号") + "，";

            if (processInRange) {
                content += "按订单列表顺序打印快递单，";
            }

            console.log({
                count,
                content: content,
                total_time: totalTime,
                context: "跨店铺打印",
            });

            collectEvent(DataTrackEvent.GENERATE_PRINT_EWAYBILL, {
                count,
                content: content,
                total_time: totalTime,
                context: "跨店铺打印",
            });
        } catch {}
    }

    rangeOrdersByShopSequence() {
        if (this.orders.length === 0) return [];

        const rangeInfo: RangeInfo[] = [];

        let rangeOrders: OrderDTO[] = [];
        let currentShopId: number = null;
        let currentPlatformType: MultiShopSelectPlatformType = null;

        for (const order of this.orders) {
            const orderPlatformType = this.orderPlatformMap.get(order.id);

            if (!currentShopId && !currentPlatformType) {
                // 第一个订单，开始新批次
                currentShopId = order.shopId;
                currentPlatformType = orderPlatformType;

                rangeOrders.push(order);
            } else if (currentShopId === order.shopId && currentPlatformType === orderPlatformType) {
                // 相同店铺，添加到当前批次
                rangeOrders.push(order);
            } else {
                // 不同店铺，结束当前批次并开始新批次
                rangeInfo.push({
                    orders: rangeOrders,
                });

                rangeOrders = [order];
                currentShopId = order.shopId;
                currentPlatformType = orderPlatformType;
            }
        }

        // 添加最后一个批次
        if (rangeOrders.length > 0) {
            rangeInfo.push({
                orders: rangeOrders,
            });
        }

        return rangeInfo;
    }

    async validateForm() {
        try {
            return await this.form.validateFields();
        } catch (error) {
            const formValues = error.values;
            const hasPrinterErrorField = error?.errorFields?.find((it) => it?.name?.includes("printerName"));

            for (const platform of platforms) {
                const template = formValues[platform]?.template;

                if (
                    hasPrinterErrorField &&
                    !formValues.isGenerateWaybillNoOnly &&
                    template &&
                    !WaybillPrinter.isReady(template)
                ) {
                    WaybillPrinter.openDownloadPluginDialog(template);
                }
            }

            return null;
        }
    }

    async validatePlatforms(formValues: PrintWaybillFormValues) {
        for (const platform of platforms) {
            const template = formValues[platform]?.template;
            const orders = this.platformOrderInfo[platform] || [];

            if (orders.length) {
                const printValidator = new PrintValidator(this.globalStore, template, orders, platform);
                const pass = await printValidator.validate();

                if (!pass) return false;
            }
        }
        return true;
    }

    async checkPrinters(formValues: PrintWaybillFormValues, isGenerateWaybillNoOnly: boolean) {
        if (!isGenerateWaybillNoOnly) {
            for (const platform of platforms) {
                const template = formValues[platform]?.template;

                if (template && !WaybillPrinter.isReady(template)) {
                    WaybillPrinter.openDownloadPluginDialog(template);
                    return false;
                }
            }
        }
        return true;
    }

    validateChildWaybillMode(
        formValues: PrintWaybillFormValues,
        copies: any,
        getWaybillNoMethod: GetWaybillNoMethod,
    ): boolean {
        if (getWaybillNoMethod === "OLD") {
            for (const platform of platforms) {
                const useChildWaybillNoMode = formValues[platform]?.useChildWaybillNoMode;

                if (copies > 1 && useChildWaybillNoMode) {
                    Modal.info({ title: "子母单不支持使用旧单号打印！" });
                    return false;
                }
            }
        }
        return true;
    }

    async confirmCopies(orders: OrderDTO[], formValues: PrintWaybillFormValues) {
        const { copies, getWaybillNoMethod } = formValues;

        if (typeof copies === "number" && copies > 1) {
            const copiesConfirmMsg =
                getWaybillNoMethod === "NEW"
                    ? `确认每个订单打印 ${copies} 份不同运单号的电子面单？`
                    : `确认每个订单使用 ${copies} 个旧运单号重打电子面单？`;

            try {
                await confirm(copiesConfirmMsg);
            } catch {
                return false;
            }
        }

        if (copies === EwaybillCopiesPreset.SPLIT_BY_WEIGHT && formValues.maxWeightPerPackage) {
            let hasOverflow = false;

            outer: for (const order of orders) {
                for (const orderItem of order.orderItems || []) {
                    if (orderItem.weight && orderItem.weight > formValues.maxWeightPerPackage * 1000) {
                        hasOverflow = true;

                        break outer;
                    }
                }
            }

            if (hasOverflow) {
                try {
                    await confirm(
                        `选择的订单中有单个商品重量超过${formValues.maxWeightPerPackage}kg，将单独打印一张快递单`,
                    );
                } catch {
                    return false;
                }
            }
        }

        return true;
    }

    calculatePrintParameters(copies: EwaybillCopiesPreset) {
        const useMaxQuantityPerPackage = copies === EwaybillCopiesPreset.USE_MAX_QUANTITY_PER_PACKAGE;
        const splitOrderItemByQuantity = copies === EwaybillCopiesPreset.SPLIT_BY_QUANTITY;
        const splitByWeight = copies === EwaybillCopiesPreset.SPLIT_BY_WEIGHT;
        const realCopies =
            useMaxQuantityPerPackage || splitOrderItemByQuantity || splitByWeight ? 1 : (copies as number);
        const packageCountType = getPackageCountTypeByCopies(copies);

        return {
            realCopies,
            packageCountType,
            useMaxQuantityPerPackage,
        };
    }

    initializePrintingProcess(
        realCopies: number,
        shippingAddress: ShippingAddressDTO,
        isGenerateWaybillNoOnly: boolean,
        useMaxQuantityPerPackage: boolean,
        processInRange: boolean,
    ) {
        this.onCancel?.();

        const processModal = this.modalStore.openModal(PrintProcessingModal, {
            current: 0,
            count: this.orders.length,
        });

        this.multiPlatformEwaybillResultStore.init({
            copies: realCopies,
            useMaxQuantityPerPackage,
            shippingAddress,
            isGenerateWaybillNoOnly,
            processInRange,
            listOrders: this.orders,
            orderPlatformMap: this.orderPlatformMap,
        });

        return processModal;
    }

    async processPlatformEwaybills({
        formValues,
        realCopies,
        isGenerateWaybillNoOnly,
        printGiftInLastWaybill,
        advancedSetting,
        packageCountType,
        getWaybillNoMethod,
        processModal,
        useMaxQuantityPerPackage,
        printBatchNo,
        specifiedSplitQuantity,
        maxWeightPerPackage,
        defaultUnitWeight,
        shippingAddressSelectType,
    }: {
        formValues: PrintWaybillFormValues;
        realCopies: number;
        isGenerateWaybillNoOnly: boolean;
        printGiftInLastWaybill: boolean;
        advancedSetting: PrintWaybillAdvancedSetting;
        packageCountType: PackageCountType;
        getWaybillNoMethod: GetWaybillNoMethod;
        processModal: ModalInstance;
        useMaxQuantityPerPackage: boolean;
        printBatchNo: string;
        specifiedSplitQuantity: number;
        maxWeightPerPackage: number;
        defaultUnitWeight: number;
        shippingAddressSelectType?: ShippingAddressSelectType;
    }) {
        const needUpdateEwaybillAccountQuantityData: Pick<
            UpdateEwaybillAccountQuantityPostData,
            "allocatedQuantity" | "ewaybillShippingAddressId"
        >[] = [];
        const waybillPrinter = new WaybillPrinter();

        let cacheProgressCurrentCountRef = { value: 0 };

        try {
            for (const platform of platforms) {
                const platformOrders = this.platformOrderInfo[platform];
                if (!platformOrders?.length) continue;

                const { template, printerName, useChildWaybillNoMode } = formValues[platform];
                const ewaybillResultStore = new EwaybillResultStore(this.globalStore);

                this.multiPlatformEwaybillResultStore[platform] = ewaybillResultStore;

                ewaybillResultStore.init({
                    orders: platformOrders,
                    copies: realCopies,
                    template,
                    printerName,
                    shippingAddress: formValues.shippingAddress,
                    isGenerateWaybillNoOnly,
                    useMaxQuantityPerPackage,
                });

                const orderGroupByShopId = groupBy(platformOrders, "shopId");
                const platformOrdersChunk = Object.values(orderGroupByShopId);
                const platformEwaybillGenerateResults: EwaybillGenerateResult[] = [];

                for (const singleShopOrders of platformOrdersChunk) {
                    const results = await waybillPrinter.generateEWaybills({
                        orders: singleShopOrders,
                        template,
                        printerName,
                        shippingAddress: formValues.shippingAddress,
                        copies: realCopies,
                        getWaybillNoMethod,
                        useChildWaybillNoType: useChildWaybillNoMode,
                        isGenerateWaybillNoOnly,
                        printGiftInLastWaybill,
                        productMaxQuantityPerPackages: useMaxQuantityPerPackage
                            ? this.globalStore.waybillPrintStore.productMaxQuantityPerPackages
                            : undefined,
                        onProgress: (current, total) => {
                            processModal.update({
                                current: cacheProgressCurrentCountRef.value + current,
                                count: this.orders.length,
                            });
                        },
                        taskId: this.taskId,
                        ignoreDuplicateOrderValidation: advancedSetting?.ignoreDuplicateOrderValidation,
                        packageCountType,
                        printBatchNo,
                        specifiedSplitQuantity,
                        maxWeightPerPackage,
                        defaultUnitWeight,
                        shippingAddressSelectType,
                    });

                    platformEwaybillGenerateResults.push(...results);
                    cacheProgressCurrentCountRef.value += singleShopOrders.length;

                    const printableOrders = waybillPrinter.buildPrintableOrders(
                        singleShopOrders,
                        platformEwaybillGenerateResults,
                    );
                    ewaybillResultStore.addPrintableOrders(printableOrders);
                }

                ewaybillResultStore.onEwaybillGenerateResultsChange(platformEwaybillGenerateResults);

                if (platformEwaybillGenerateResults?.length) {
                    if (getWaybillNoMethod === GetWaybillNoMethod.NEW) {
                        let allocatedQuantity = 0;
                        platformEwaybillGenerateResults.forEach((result) => {
                            allocatedQuantity += result.ewaybills.length;
                        });

                        if (allocatedQuantity && template?.ewaybillShippingAddressId) {
                            needUpdateEwaybillAccountQuantityData.push({
                                allocatedQuantity,
                                ewaybillShippingAddressId: template.ewaybillShippingAddressId,
                            });
                        }

                        this.updateOrderTable(
                            platform,
                            platformOrders,
                            template,
                            platformEwaybillGenerateResults,
                            false,
                            isGenerateWaybillNoOnly,
                        );
                    } else if (getWaybillNoMethod === GetWaybillNoMethod.OLD) {
                        this.logRePrint(
                            platform,
                            platformEwaybillGenerateResults,
                            template,
                            realCopies,
                            isGenerateWaybillNoOnly,
                            false,
                            advancedSetting?.ignoreDuplicateOrderValidation,
                            printerName,
                            shippingAddressSelectType,
                        );
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }

        //  更新电子面单账号余额
        this.batchUpdateEwaybillQuantity(needUpdateEwaybillAccountQuantityData);

        // 设置各个平台打印开始序号和总数
        this.setupEwaybillResultDocumentNumbers();
    }

    async processPlatformEwaybillsInRange({
        formValues,
        realCopies,
        isGenerateWaybillNoOnly,
        printGiftInLastWaybill,
        advancedSetting,
        packageCountType,
        getWaybillNoMethod,
        processModal,
        useMaxQuantityPerPackage,
        printBatchNo,
        specifiedSplitQuantity,
        maxWeightPerPackage,
        defaultUnitWeight,
        shippingAddressSelectType,
    }: {
        formValues: PrintWaybillFormValues;
        realCopies: number;
        isGenerateWaybillNoOnly: boolean;
        printGiftInLastWaybill: boolean;
        advancedSetting: PrintWaybillAdvancedSetting;
        packageCountType: PackageCountType;
        getWaybillNoMethod: GetWaybillNoMethod;
        processModal: ModalInstance;
        useMaxQuantityPerPackage: boolean;
        printBatchNo: string;
        specifiedSplitQuantity: number;
        maxWeightPerPackage: number;
        defaultUnitWeight: number;
        shippingAddressSelectType?: ShippingAddressSelectType;
    }) {
        const needUpdateEwaybillAccountQuantityData: Pick<
            UpdateEwaybillAccountQuantityPostData,
            "allocatedQuantity" | "ewaybillShippingAddressId"
        >[] = [];
        const waybillPrinter = new WaybillPrinter();
        const rangeInfos = this.rangeOrdersByShopSequence();

        let cacheProgressCurrentCountRef = { value: 0 };
        let totalDocumentCount = 0;
        let firstDocumentNumber = 1;

        try {
            for (const rangeInfo of rangeInfos) {
                const platformEwaybillGenerateResults: EwaybillGenerateResult[] = [];

                if (!rangeInfo?.orders?.length) continue;
                const { orders: rangeOrders } = rangeInfo;
                const firstOrder = rangeOrders[0];

                const platform = this.orderPlatformMap.get(firstOrder.id);
                if (!platform) continue;

                const { template, printerName, useChildWaybillNoMode } = formValues[platform];

                let ewaybillResultStore: EwaybillResultStore = this.multiPlatformEwaybillResultStore[platform];

                if (!ewaybillResultStore) {
                    ewaybillResultStore = new EwaybillResultStore(this.globalStore);

                    this.multiPlatformEwaybillResultStore[platform] = ewaybillResultStore;

                    ewaybillResultStore.init({
                        orders: [],
                        copies: realCopies,
                        template,
                        printerName,
                        shippingAddress: formValues.shippingAddress,
                        isGenerateWaybillNoOnly,
                        useMaxQuantityPerPackage,
                    });
                }

                ewaybillResultStore.addOrders(rangeOrders);

                const results = await waybillPrinter.generateEWaybills({
                    orders: rangeOrders,
                    template,
                    printerName,
                    shippingAddress: formValues.shippingAddress,
                    copies: realCopies,
                    getWaybillNoMethod,
                    useChildWaybillNoType: useChildWaybillNoMode,
                    isGenerateWaybillNoOnly,
                    printGiftInLastWaybill,
                    productMaxQuantityPerPackages: useMaxQuantityPerPackage
                        ? this.globalStore.waybillPrintStore.productMaxQuantityPerPackages
                        : undefined,
                    onProgress: (current, total) => {
                        processModal.update({
                            current: cacheProgressCurrentCountRef.value + current,
                            count: this.orders.length,
                        });
                    },
                    taskId: this.taskId,
                    ignoreDuplicateOrderValidation: advancedSetting?.ignoreDuplicateOrderValidation,
                    packageCountType,
                    printBatchNo,
                    specifiedSplitQuantity,
                    maxWeightPerPackage,
                    shippingAddressSelectType,
                    defaultUnitWeight,
                });

                platformEwaybillGenerateResults.push(...results);
                cacheProgressCurrentCountRef.value += rangeOrders.length;

                const printableOrders = waybillPrinter.buildPrintableOrders(rangeOrders, results);
                ewaybillResultStore.addPrintableOrders(printableOrders);
                ewaybillResultStore.addEwaybillGenerateResults(platformEwaybillGenerateResults, rangeOrders);

                rangeInfo.firstDocumentNumber = firstDocumentNumber;
                rangeInfo.printableOrders = printableOrders;

                totalDocumentCount += printableOrders.length;
                firstDocumentNumber += printableOrders.length;

                if (platformEwaybillGenerateResults?.length) {
                    if (getWaybillNoMethod === GetWaybillNoMethod.NEW) {
                        let allocatedQuantity = 0;
                        platformEwaybillGenerateResults.forEach((result) => {
                            allocatedQuantity += result.ewaybills.length;
                        });

                        if (allocatedQuantity && template?.ewaybillShippingAddressId) {
                            needUpdateEwaybillAccountQuantityData.push({
                                allocatedQuantity,
                                ewaybillShippingAddressId: template.ewaybillShippingAddressId,
                            });
                        }

                        console.log("rangeOrders:", cloneDeep(rangeOrders));
                        console.log("platformEwaybillGenerateResults:", cloneDeep(platformEwaybillGenerateResults));

                        this.updateOrderTable(
                            platform,
                            rangeOrders,
                            template,
                            platformEwaybillGenerateResults,
                            false,
                            isGenerateWaybillNoOnly,
                        );
                    } else if (getWaybillNoMethod === GetWaybillNoMethod.OLD) {
                        this.logRePrint(
                            platform,
                            platformEwaybillGenerateResults,
                            template,
                            realCopies,
                            isGenerateWaybillNoOnly,
                            false,
                            advancedSetting?.ignoreDuplicateOrderValidation,
                            printerName,
                            shippingAddressSelectType,
                        );
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }

        //  更新电子面单账号余额
        this.batchUpdateEwaybillQuantity(needUpdateEwaybillAccountQuantityData);

        // 记录电子面单生成序号
        this.setupEwaybillResultDocumentNumbersByRange(rangeInfos, totalDocumentCount);

        return rangeInfos;
    }

    setupEwaybillResultDocumentNumbers() {
        let firstDocumentNumber = 1;
        let totalDocumentCount = platforms.reduce<number>((total, platform) => {
            const ewaybillResultStore = this.multiPlatformEwaybillResultStore[platform];
            if (!ewaybillResultStore) return total;

            const printableOrders = this.multiPlatformEwaybillResultStore[platform].printableOrders;
            return total + (printableOrders?.length || 0);
        }, 0);

        for (const platform of platforms) {
            const ewaybillResultStore = this.multiPlatformEwaybillResultStore[platform];
            if (!ewaybillResultStore) continue;

            const printableOrders = this.multiPlatformEwaybillResultStore[platform].printableOrders;
            ewaybillResultStore.setFirstDocumentNumber(firstDocumentNumber);
            ewaybillResultStore.setTotalDocumentCount(totalDocumentCount);
            firstDocumentNumber += printableOrders.length;
            ewaybillResultStore.recordOrderPrintableDocumentNumber(printableOrders);
        }
    }

    setupEwaybillResultDocumentNumbersByRange(rangeInfos: RangeInfo[], totalDocumentCount: number) {
        for (const rangeInfo of rangeInfos) {
            rangeInfo.totalDocumentCount = totalDocumentCount;

            const { orders, printableOrders } = rangeInfo;
            const firstOrder = orders?.[0];

            const platform = this.orderPlatformMap.get(firstOrder?.id);
            if (!platform) continue;
            const ewaybillAccountStore = this.multiPlatformEwaybillResultStore[platform];
            if (!ewaybillAccountStore) continue;

            ewaybillAccountStore.setTotalDocumentCount(totalDocumentCount);
            rangeInfo.totalDocumentCount = totalDocumentCount;
            ewaybillAccountStore.recordOrderPrintableDocumentNumber(printableOrders, rangeInfo.firstDocumentNumber);
        }
    }

    openPrintResultModal() {
        this.modalStore.openModal(PrintEwaybillResultModal, {
            store: this.multiPlatformEwaybillResultStore,
            onTaskDone: this.onShipTaskDone,
            onUpdateOrderSellerRemarks: this.onUpdateOrderSellerRemarks,
            onUpdateSupplierOrderRemarks: this.onUpdateSupplierOrderRemarks,
        });
    }

    async executePrinting(realCopies: number, shippingAddress: ShippingAddressDTO) {
        const waybillPrinter = new WaybillPrinter();

        for (const platform of platforms) {
            const ewaybillResultStore: EwaybillResultStore = this.multiPlatformEwaybillResultStore[platform];
            if (!ewaybillResultStore) continue;

            const printableOrders = this.multiPlatformEwaybillResultStore[platform].printableOrders;
            const printerName = this.multiPlatformEwaybillResultStore[platform].printerName;
            if (!printableOrders?.length) continue;

            const firstDocumentNumber = ewaybillResultStore.firstDocumentNumber;
            const totalDocumentCount = ewaybillResultStore.totalDocumentCount;
            const template = this.multiPlatformEwaybillResultStore[platform].template;

            await waybillPrinter.print({
                printableOrders,
                template,
                printerName,
                shippingAddress,
                copies: realCopies,
                isNeedShowPrintResult: true,
                firstDocumentNumber,
                totalDocumentCount,
                waitingPrinted: true,
                onPrintResultChange: (waybillPrintResults) => {
                    ewaybillResultStore.addWaybillPrintResults(waybillPrintResults);
                },
            });
        }
    }

    async executeRangePrinting(realCopies: number, shippingAddress: ShippingAddressDTO, rangeInfos: RangeInfo[]) {
        const waybillPrinter = new WaybillPrinter();

        for (const rangeInfo of rangeInfos) {
            const { orders, printableOrders, firstDocumentNumber, totalDocumentCount } = rangeInfo;

            if (!printableOrders?.length) continue;

            const firstOrder = orders[0];
            const platform = this.orderPlatformMap.get(firstOrder.id);
            if (!platform) continue;

            const printerName = this.multiPlatformEwaybillResultStore[platform].printerName;
            const template = this.multiPlatformEwaybillResultStore[platform].template;
            const ewaybillResultStore: EwaybillResultStore = this.multiPlatformEwaybillResultStore[platform];

            await waybillPrinter.print({
                printableOrders,
                template,
                printerName,
                shippingAddress,
                copies: realCopies,
                isNeedShowPrintResult: true,
                firstDocumentNumber,
                totalDocumentCount,
                waitingPrinted: true,
                onPrintResultChange: (waybillPrintResults) => {
                    ewaybillResultStore.addWaybillPrintResults(waybillPrintResults);
                },
            });
        }
    }
}

export default MultiPlatformPrintWaybillModalStore;
